// Times and days as the event's own room clocks read them.
//
// The fleet phones are set up in Europe and carried to the event, so the handset's locale
// and zone say nothing about the room it stands in. A door phone at San Jose showed
// "13:15" and would show the wrong hour altogether if its zone never followed it. Every
// time on screen is therefore formatted in the event's zone, which the server sends with
// the session, and in the event's convention: AM/PM for an event in the Americas, the
// phone's own locale anywhere else.
//
// With no zone known (a phone paired before the server sent one and not opened online
// since) it falls back to the phone, which is what every build before this one did.
//
// The imports are written out rather than left to Nuxt, which is what lets the test load
// this file with plain Node.

// The locale an event's times are written in. The Americas read a 12 hour clock; every
// other event WAD runs follows the phone.
const eventLocale = (timeZone?: string | null): string | undefined =>
  timeZone?.startsWith('America/') ? 'en-US' : undefined

// An invalid zone makes Intl throw, and a blank door screen is worse than a clock in the
// phone's own zone.
const format = (value: string, timeZone: string | null | undefined, options: Intl.DateTimeFormatOptions): string => {
  const date = new Date(value)

  try {
    return date.toLocaleString(eventLocale(timeZone), { ...options, timeZone: timeZone || undefined })
  } catch {
    return date.toLocaleString(undefined, options)
  }
}

/**
 * A clock time, "1:15 PM" at a US event.
 *
 * hour is 'numeric' rather than '2-digit' on purpose: 2-digit forces a leading zero that a
 * 12 hour clock never uses, which is where "01:00 PM" came from.
 */
export const eventClock = (value: string, timeZone?: string | null): string =>
  format(value, timeZone, { hour: 'numeric', minute: '2-digit' })

/** The day a sitting runs on, "Wed, Sep 23" at a US event. */
export const eventDay = (value: string, timeZone?: string | null): string =>
  format(value, timeZone, { weekday: 'short', day: 'numeric', month: 'short' })
